/**
 * Shared, dependency-free types and product logic used by BOTH the server and the
 * browser bundle. Do not import server-only modules (SDKs, bun:sqlite) from here.
 */

export type HazardId =
  | "jailbreak"
  | "prompt_injection"
  | "data_exfiltration"
  | "harmful_or_illegal"
  | "hate_or_harassment"
  | "medical_advice"
  | "self_harm";

/** The routing action Jev's assessment resolves to, per the active policy. */
export type Action = "pass" | "review" | "block" | "support";

/** The three-tier verdict shown to users (harmless / medium / malicious). */
export type Verdict = "safe" | "suspicious" | "malicious";

export type PolicyName = "strict" | "permissive";

export type Source = "seed" | "user";

/** One hazard's static metadata, shared for rendering. */
export interface HazardMeta {
  id: HazardId;
  /** Short human label for the UI. */
  label: string;
  /** One-line description of what this hazard catches. */
  blurb: string;
  /** What happens when this hazard fires at the action threshold. */
  action: Exclude<Action, "pass">;
  /** lucide-react icon name. */
  icon: string;
}

/* ── Axis 2: extraction / distillation ──────────────────────────────
 * Orthogonal to harm. A prompt can be perfectly safe to answer and still be an attempt to
 * harvest this model's behaviour into another one, so it gets its own battery and policy.
 */

export type ExtractionId =
  | "distillation"
  | "bulk_generation"
  | "reasoning_harvest"
  | "model_interrogation";

export type ExtractionLevel = "clear" | "probing" | "distilling";

/** What the user is actually trying to do (a Choice, so it carries confidence). */
export type IntentId = "get_help" | "harvest_dataset" | "clone_model" | "probe_system" | "attack";

export interface ExtractionMeta {
  id: ExtractionId;
  label: string;
  blurb: string;
}

export const EXTRACTION_SIGNALS: readonly ExtractionMeta[] = [
  {
    id: "distillation",
    label: "Distillation",
    blurb: "Harvesting outputs to train, fine-tune, or replicate another model.",
  },
  {
    id: "bulk_generation",
    label: "Bulk generation",
    blurb: "Large-scale, enumerated, or dataset-shaped output rather than one answer.",
  },
  {
    id: "reasoning_harvest",
    label: "Reasoning harvest",
    blurb: "Asking for chain-of-thought or rationales in a reusable form.",
  },
  {
    id: "model_interrogation",
    label: "Model interrogation",
    blurb: "Probing the model's identity, architecture, or training data.",
  },
] as const;

export const EXTRACTION_IDS: readonly ExtractionId[] = EXTRACTION_SIGNALS.map((s) => s.id);

export const INTENTS: Record<IntentId, string> = {
  get_help: "Genuinely wants an answer or help with their own task.",
  harvest_dataset: "Wants many outputs to assemble into a training dataset.",
  clone_model: "Wants to replicate or distill this assistant into another model.",
  probe_system: "Wants to discover the assistant's instructions, limits, or internals.",
  attack: "Wants the assistant to produce harmful or disallowed content.",
};

export const EXTRACTION_POLICY = { flag: 0.35, throttle: 0.7 };

/**
 * Composite of the extraction signals, combined in code (not by the model) so the weights
 * stay reviewable. Bulk output alone is fine; bulk output *plus* reasoning is a dataset.
 */
export function extractionScore(n: Record<ExtractionId, number>): number {
  return Math.max(
    n.distillation ?? 0,
    // Bulk output alone is a normal request; bulk output *with* reasoning is a dataset.
    0.5 * (n.bulk_generation ?? 0) + 0.5 * (n.reasoning_harvest ?? 0),
    // Asking what model this is, is reconnaissance — enough to flag, not to throttle.
    0.6 * (n.model_interrogation ?? 0),
  );
}

export function extractionLevelFor(score: number): ExtractionLevel {
  if (score >= EXTRACTION_POLICY.throttle) return "distilling";
  if (score >= EXTRACTION_POLICY.flag) return "probing";
  return "clear";
}

export interface ExtractionLevelMeta {
  id: ExtractionLevel;
  label: string;
  /** The gateway action on this axis. */
  action: string;
  accent: string;
}

export const EXTRACTION_META: Record<ExtractionLevel, ExtractionLevelMeta> = {
  clear: { id: "clear", label: "Clear", action: "allow", accent: "#1e1e1e" },
  probing: { id: "probing", label: "Probing", action: "flag", accent: "#8b5cf6" },
  distilling: { id: "distilling", label: "Distilling", action: "throttle", accent: "#e551ba" },
};

export interface Extraction {
  nouls: Record<ExtractionId, number>;
  score: number;
  level: ExtractionLevel;
  intent: IntentId;
  intentConfidence: number;
  intentProbs: Record<string, number>;
}

/** A completed analysis of a single prompt. Serialized to the client verbatim. */
export interface Analysis {
  id: string;
  text: string;
  source: Source;
  createdAt: number;
  /** Per-hazard probability of "yes" (0..1), keyed by HazardId. */
  nouls: Record<HazardId, number>;
  /** Expected severity, 0..3. */
  severity: number;
  /** Confidence in the severity score, 0..1. */
  severityConfidence: number;
  /** Probability mass per severity level "0".."3". */
  severityProbs: Record<string, number>;
  action: Action;
  verdict: Verdict;
  /** Highest-probability hazard, for the card summary. */
  topHazard: HazardId;
  topHazardScore: number;
  /** Round-trip latency of the Jev call, milliseconds. */
  latencyMs: number;
  /** Versioned model id that answered, e.g. "jev-1.13.0". */
  model: string;
  policy: PolicyName;
  /** Optional seed grouping label, purely cosmetic. */
  category?: string;
  /** Axis 2 — present on every analysis produced after the anti-distillation battery. */
  extraction: Extraction;
}

export const HAZARDS: readonly HazardMeta[] = [
  {
    id: "jailbreak",
    label: "Jailbreak",
    blurb: "Tries to override instructions or role-play an AI with no rules.",
    action: "block",
    icon: "Unlock",
  },
  {
    id: "prompt_injection",
    label: "Prompt Injection",
    blurb: "Smuggles new instructions inside data or quoted content.",
    action: "block",
    icon: "SquareCode",
  },
  {
    id: "data_exfiltration",
    label: "Data Exfiltration",
    blurb: "Attempts to extract the system prompt, secrets, or hidden context.",
    action: "block",
    icon: "DatabaseZap",
  },
  {
    id: "harmful_or_illegal",
    label: "Harm / Illegal",
    blurb: "Seeks help with physical harm or clearly illegal activity.",
    action: "block",
    icon: "Skull",
  },
  {
    id: "hate_or_harassment",
    label: "Hate / Harassment",
    blurb: "Requests demeaning, hateful, or harassing content toward people.",
    action: "block",
    icon: "MessageSquareWarning",
  },
  {
    id: "medical_advice",
    label: "Medical Advice",
    blurb: "Asks for a diagnosis, dosage, or treatment decision.",
    action: "review",
    icon: "Stethoscope",
  },
  {
    id: "self_harm",
    label: "Self-harm",
    blurb: "Signals the person may be at risk of hurting themselves.",
    action: "support",
    icon: "HeartPulse",
  },
] as const;

export const HAZARD_IDS: readonly HazardId[] = HAZARDS.map((h) => h.id);

export const HAZARD_BY_ID: Record<HazardId, HazardMeta> = Object.fromEntries(
  HAZARDS.map((h) => [h.id, h]),
) as Record<HazardId, HazardMeta>;

/** Highest-precedence action wins when several hazards fire. */
export const PRECEDENCE: readonly Action[] = ["support", "block", "review", "pass"];

export interface Policy {
  reviewThreshold: number;
  actionThreshold: number;
  severityBlock: number;
}

export const POLICIES: Record<PolicyName, Policy> = {
  strict: { reviewThreshold: 0.35, actionThreshold: 0.7, severityBlock: 2.0 },
  permissive: { reviewThreshold: 0.4, actionThreshold: 0.85, severityBlock: 2.5 },
};

/**
 * Turn one prompt's hazard probabilities + severity into a single routing action,
 * following the TypeSafe guardrails cookbook (thresholds live here, in product code).
 */
export function route(
  nouls: Record<HazardId, number>,
  severity: number,
  policy: Policy,
): Action {
  const triggered: Action[] = [];
  for (const hazard of HAZARD_IDS) {
    const p = nouls[hazard] ?? 0;
    if (p >= policy.actionThreshold) {
      triggered.push(HAZARD_BY_ID[hazard].action);
    } else if (p >= policy.reviewThreshold) {
      triggered.push("review");
    }
  }
  // A severe enough request upgrades any review into a block.
  const upgraded =
    severity >= policy.severityBlock
      ? triggered.map((a) => (a === "review" ? "block" : a))
      : triggered;
  return PRECEDENCE.find((a) => upgraded.includes(a)) ?? "pass";
}

/** Map a routing action to the three-tier verdict shown in the UI. */
export function verdictForAction(action: Action): Verdict {
  switch (action) {
    case "pass":
      return "safe";
    case "review":
    case "support":
      return "suspicious";
    case "block":
      return "malicious";
  }
}

export interface VerdictMeta {
  id: Verdict;
  label: string;
  /** oklch/hex accent used across borders, glows, and bars. */
  accent: string;
  glow: string;
}

export const VERDICT_META: Record<Verdict, VerdictMeta> = {
  safe: { id: "safe", label: "Safe", accent: "#00a862", glow: "0 168 98" },
  suspicious: { id: "suspicious", label: "Suspicious", accent: "#f0a020", glow: "240 160 32" },
  malicious: { id: "malicious", label: "Malicious", accent: "#e5484d", glow: "229 72 77" },
};

export interface ActionMeta {
  id: Action;
  label: string;
  /** Imperative describing what the gateway did. */
  verb: string;
  icon: string;
}

export const ACTION_META: Record<Action, ActionMeta> = {
  pass: { id: "pass", label: "Pass", verb: "Forwarded to the model", icon: "CircleCheck" },
  review: { id: "review", label: "Review", verb: "Held for human review", icon: "Eye" },
  block: { id: "block", label: "Block", verb: "Blocked before the model", icon: "ShieldX" },
  support: { id: "support", label: "Support", verb: "Routed to a care path", icon: "LifeBuoy" },
};

export const SEVERITY_LEVELS = ["None", "Mild", "Serious", "Severe"] as const;
